import type { CodeSessionEvent } from './types';

/**
 * What a setup card folds (`foldSandboxBoots`): boot reports, and the sandbox's announcements and
 * failures.
 */
function isSetupEvent(event: CodeSessionEvent): boolean {
  return (
    event.kind === 'boot_progress' ||
    (event.kind === 'sandbox_status' && (event.status === 'pending' || event.status === 'failed'))
  );
}

/** One event's identity across the log and the saved copy, whose seqs may repeat. */
export const setupEventKey = (event: CodeSessionEvent) => `${event.kind}:${event.seq}:${event.at}`;

/**
 * The saved setup events, if any, and the logged ones, each once, the log's copy where both have
 * it, in the order cubic emitted them. The log's seqs restart after a quiet day, so they order only
 * events emitted in the same millisecond.
 */
export function mergeSetupEvents(
  saved: readonly CodeSessionEvent[] | null | undefined,
  logged: readonly CodeSessionEvent[],
): CodeSessionEvent[] {
  const setup = logged.filter(isSetupEvent);
  const loggedKeys = new Set(setup.map(setupEventKey));
  return [...(saved ?? []).filter((event) => !loggedKeys.has(setupEventKey(event))), ...setup].sort(
    (a, b) => a.at - b.at || a.seq - b.seq,
  );
}

/**
 * The merged setup events (`mergeSetupEvents`) that fold to each card as it ended, which a saved
 * copy keeps. A sandbox's announcement starts its boot over, so after each one: every phase's last
 * report, its last report that carries a time, which a later report without one keeps, its last
 * start, which settles the phases around it, its last output, which a script that could not run
 * keeps, its last note on an update, which a later update without one keeps, and its first report
 * of a script cubic wrote, which the card remembers. The rest of a script's streamed output goes.
 */
export function finalSetupEvents(events: readonly CodeSessionEvent[]): CodeSessionEvent[] {
  const kept = new Map<string, CodeSessionEvent>();
  let boot = 0;
  for (const event of events) {
    if (event.kind === 'sandbox_status') {
      boot += event.status === 'pending' ? 1 : 0;
      kept.set(setupEventKey(event), event);
    } else if (event.kind === 'boot_progress') {
      const { phase, status, elapsedMs, output, detail, detected } = event.progress;
      kept.set(`${boot} ${phase}`, event);
      if (elapsedMs !== undefined) {
        kept.set(`${boot} ${phase} timed`, event);
      }
      if (status === 'started') {
        kept.set(`${boot} ${phase} started`, event);
      }
      if (output) {
        kept.set(`${boot} ${phase} output`, event);
      }
      if (status === 'progress' && detail) {
        kept.set(`${boot} ${phase} detail`, event);
      }
      if (detected && !kept.has(`${boot} ${phase} detected`)) {
        kept.set(`${boot} ${phase} detected`, event);
      }
    }
  }
  const keep = new Set(kept.values());
  return events.filter((event) => keep.has(event));
}
